<template>
  <div class="sincere-home">
    <van-nav-bar fixed placeholder class="home-nav" :border="false">
      <template #left>
        <img class="nav-logo" :src="assets.logo" :alt="$t('ui.brand.name')">
      </template>
      <template #title>
        <span class="nav-title">{{ $t('ui.home.title') }}</span>
      </template>
      <template #right>
        <img
          class="nav-action-icon"
          :src="assets.topVideoIcon"
          :alt="$t('ui.nav.video')"
          @click="toRoute('/Video')"
        >
      </template>
    </van-nav-bar>

    <div class="home-body">
      <van-swipe
        class="banner-swipe"
        :autoplay="3000"
        indicator-color="#e6b98a"
      >
        <van-swipe-item
          v-for="(item, index) in heroBanners"
          :key="index"
          class="banner-item"
        >
          <img :src="item.url" :alt="String(index + 1)" class="banner-image" loading="lazy">
        </van-swipe-item>
      </van-swipe>

      <section class="notice-section">
        <div class="notice-container">
          <img :src="assets.noticeIcon" alt="notice" class="notice-icon">
          <div class="notice-marquee">
            <div class="notice-track">
              <span>{{ notice }}</span>
              <span>{{ notice }}</span>
            </div>
          </div>
          <button class="more-btn" @click="toRoute('/Notice')">{{ $t('ui.common.more') }}</button>
        </div>
      </section>

      <section class="shortcut-section">
        <div class="shortcut-grid">
          <button
            v-for="item in shortcutItems"
            :key="item.title"
            class="shortcut-item"
            @click="toRoute(item.path)"
          >
            <img :src="item.icon" :alt="item.title" class="shortcut-icon" loading="lazy">
            <span>{{ item.title }}</span>
          </button>
        </div>
      </section>

      <section class="section-title">
        <div class="title-left">
          <span class="title-line"></span>
          <span class="title-text">{{ $t('ui.home.online_companions') }}</span>
        </div>
        <button class="more-btn secondary" @click="toRoute('/Choose')">{{ $t('ui.common.more') }}</button>
      </section>

      <section class="dating-grid">
        <article
          v-for="item in featuredProfiles"
          :key="item.id"
          class="dating-card"
          @click="toDetail(item.id)"
        >
          <img :src="item.img_url" :alt="item.idText" class="dating-image" loading="lazy">
          <img :src="assets.liveIcon" alt="live" class="live-icon" loading="lazy">
          <div class="verified-tag">{{ item.verifiedText }}</div>

          <div class="card-overlay">
            <div class="card-top">
              <span class="vip-text">{{ item.idText }}</span>
              <span class="location-tag">{{ item.locationText }}</span>
            </div>

            <div class="score-row">
              <img :src="assets.scoreIcon" alt="score" class="score-icon">
              <span class="score-text">{{ item.scoreText }}</span>
            </div>
          </div>
        </article>
      </section>
    </div>

    <button class="customer-service-btn" @click="openSupport()">
      <img src="/img/mine/kefu.png" alt="support">
    </button>
  </div>
</template>

<script>
const ASSETS = {
  logo: '/img/login/sincere-logo.png',
  topVideoIcon: '/img/neww/ico_video1.png',
  noticeIcon: '/img/neww/notice.png',
  liveIcon: 'https://m.sincereloves.online/icon/lives.gif',
  scoreIcon: '/img/neww/icon_love.png',
  eventsIcon: '/img/neww/ico_vote.png',
  termsIcon: 'https://m.sincereloves.online/icon/icon_activities.png',
  videoIcon: '/img/neww/ico_video.png',
  datingIcon: '/img/neww/ico_vip.png'
};

export default {
  data() {
    return {
      assets: ASSETS,
      notice: '',
      banners: [],
      scrollbarStyleId: 'home-scrollbar-style',
      datalist: []
    };
  },
  computed: {
    shortcutItems() {
      return [
        { title: this.$t('ui.nav.events'), path: '/Lottery', icon: ASSETS.eventsIcon },
        { title: this.$t('ui.nav.terms'), path: '/Notice', icon: ASSETS.termsIcon },
        { title: this.$t('ui.nav.video'), path: '/Video', icon: ASSETS.videoIcon },
        { title: this.$t('ui.nav.dating'), path: '/Choose', icon: ASSETS.datingIcon }
      ];
    },
    heroBanners() {
      return this.banners;
    },
    featuredProfiles() {
      return this.datalist.map((source, index) => {
        const safeSource = source && typeof source === 'object' ? source : {};
        const scoreNumber = Number(safeSource.number);
        return {
          ...safeSource,
          id: safeSource.id || index + 1,
          img_url: this.normalizeAssetUrl(safeSource.img_url || ''),
          idText: (safeSource.home_vip_text || '').trim() || `VIP${safeSource.id || index + 1}`,
          locationText: (safeSource.flag || '').trim() || this.$t('ui.common.default_location'),
          scoreText: Number.isFinite(scoreNumber) && scoreNumber > 0 ? scoreNumber.toFixed(1) : '9.9',
          verifiedText: (safeSource.home_verified_text || '').trim() || this.$t('ui.common.verified')
        };
      });
    }
  },
  methods: {
    getBasicConfig() {
      const lang = localStorage.getItem('lang') || 'ja';
      this.$http({
        method: 'get',
        url: 'sys_config',
        data: { lang }
      }).then((res) => {
        const data = res && res.data ? res.data : {};
        this.notice = String(data.notice || '').trim();
        this.banners = this.normalizeBannerList(data.banners);
      }).catch(() => {
        this.notice = '';
        this.banners = [];
      });
    },
    normalizeBannerList(list) {
      if (!Array.isArray(list)) {
        return [];
      }

      return list.map((item, index) => {
        const rawUrl = typeof item === 'string'
          ? item
          : (item && typeof item === 'object' ? item.url || item.image || item.img : '');

        return {
          ...((item && typeof item === 'object') ? item : {}),
          url: this.normalizeBannerUrl(rawUrl),
          id: (item && typeof item === 'object' && item.id) ? item.id : index + 1
        };
      }).filter(item => item.url);
    },
    normalizeBannerUrl(url) {
      const value = String(url || '').trim();
      if (!value) {
        return '';
      }

      if (/^https?:\/\//i.test(value)) {
        return value;
      }

      if (value.startsWith('//')) {
        return `https:${value}`;
      }

      if (value.startsWith('/')) {
        return this.normalizeAssetUrl(value);
      }

      return `https://${value.replace(/^\/+/, '')}`;
    },
    toRoute(path) {
      this.$router.push({ path });
    },
    toDetail(id) {
      this.$router.push({ path: '/detail', query: { id } });
    },
    openSupport() {
      const service = this.$store.getters.getBaseInfo || {};
      if (service.iskefu == 1 && service.kefu) {
        window.location.href = service.kefu;
        return;
      }
      this.$router.push('/ServiceOnline');
    },
    hideRootScrollbar() {
      if (typeof document === 'undefined') {
        return;
      }

      const root = document.documentElement;
      const body = document.body;

      root.style.setProperty('scrollbar-width', 'none');
      body.style.setProperty('scrollbar-width', 'none');
      root.style.setProperty('-ms-overflow-style', 'none');
      body.style.setProperty('-ms-overflow-style', 'none');

      if (!document.getElementById(this.scrollbarStyleId)) {
        const style = document.createElement('style');
        style.id = this.scrollbarStyleId;
        style.textContent = `
html::-webkit-scrollbar,
body::-webkit-scrollbar {
  width: 0 !important;
  height: 0 !important;
  display: none !important;
}
        `;
        document.head.appendChild(style);
      }
    },
    restoreRootScrollbar() {
      if (typeof document === 'undefined') {
        return;
      }

      const root = document.documentElement;
      const body = document.body;
      const style = document.getElementById(this.scrollbarStyleId);

      root.style.removeProperty('scrollbar-width');
      body.style.removeProperty('scrollbar-width');
      root.style.removeProperty('-ms-overflow-style');
      body.style.removeProperty('-ms-overflow-style');

      if (style && style.parentNode) {
        style.parentNode.removeChild(style);
      }
    },
    normalizeAssetUrl(url) {
      if (!url || typeof url !== 'string') {
        return '';
      }

      const origin = 'https://admin699.ltalk6.com';

      if (/^https?:\/\/127\.0\.0\.1\//.test(url)) {
        return url.replace(/^https?:\/\/127\.0\.0\.1\//, `${origin}/`);
      }

      if (/^https?:\/\/localhost:8081\//.test(url)) {
        return url.replace(/^https?:\/\/localhost:8081\//, `${origin}/`);
      }

      if (url.startsWith('/')) {
        return `${origin}${url}`;
      }

      return url;
    },
    getxuanfeilist() {
      this.$http({
        method: 'get',
        url: 'xuanfeilist',
        data: { id: 1, page: 1 }
      }).then((res) => {
        const lang = localStorage.getItem('lang') || 'zh';
        const list = Array.isArray(res && res.data) ? res.data : [];
        this.datalist = list.map((item) => {
          const safeItem = item && typeof item === 'object' ? item : {};
          const rawFlag = typeof safeItem.flag === 'string' ? safeItem.flag : '';
          const parts = rawFlag ? rawFlag.split('|') : [];
          let localizedFlag = parts[0] || '';

          if (lang === 'en') {
            localizedFlag = parts[1] || localizedFlag;
          } else if (lang === 'ja') {
            localizedFlag = parts[2] || localizedFlag;
          } else if (lang !== 'zh') {
            localizedFlag = parts[3] || localizedFlag;
          }

          return {
            ...safeItem,
            flag: localizedFlag || this.$t('ui.common.default_location')
          };
        });
      });
    }
  },
  mounted() {
    this.hideRootScrollbar();
  },
  beforeDestroy() {
    this.restoreRootScrollbar();
  },
  created() {
    this.getBasicConfig();
    this.getxuanfeilist();
  }
};
</script>

<style lang="less" scoped>
.sincere-home {
  min-height: 100vh;
  background: #000;
  color: #fff;
}

.home-nav {
  min-height: 98px;
  background: #1c1c1e;
}

::v-deep .home-nav .van-nav-bar {
  background: #1c1c1e;
}

::v-deep .home-nav .van-nav-bar__content {
  height: 88px;
}

::v-deep .home-nav .van-nav-bar__left,
::v-deep .home-nav .van-nav-bar__right {
  padding: 0 18px;
}

.nav-logo {
  width: 58px;
  height: 58px;
  object-fit: contain;
}

.nav-title {
  color: #e6b98a;
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
}

.nav-action-icon {
  width: 38px;
  height: 38px;
  object-fit: contain;
}

.home-body {
  padding: 10px 0 108px;
}

.banner-swipe {
  margin-bottom: 28px;
}

.banner-item,
.banner-image {
  width: 100%;
  height: 278px;
}

.banner-image {
  display: block;
  object-fit: cover;
  border-radius: 20px;
}

::v-deep .banner-swipe .van-swipe__indicators {
  bottom: 16px;
}

::v-deep .banner-swipe .van-swipe__indicator {
  width: 7px;
  height: 7px;
  margin: 0 4px;
  background: rgba(255, 255, 255, 0.45);
}

.notice-section,
.shortcut-section,
.section-title,
.dating-grid {
  padding: 0 12px;
}

.notice-container,
.shortcut-grid {
  border: 2px solid #766350;
  background: linear-gradient(90deg, rgba(37, 38, 43, 0.95), rgba(29, 30, 36, 0.95));
}

.notice-container {
  display: flex;
  align-items: center;
  height: 78px;
  padding: 0 18px;
  border-radius: 18px;
}

.notice-icon {
  width: 38px;
  height: 38px;
  margin-right: 14px;
  object-fit: contain;
}

.notice-marquee {
  flex: 1;
  overflow: hidden;
  color: #efc79b;
  font-size: 22px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
}

.notice-track {
  display: inline-flex;
  min-width: max-content;
  animation: marquee 32s linear infinite;
}

.notice-track span {
  padding-right: 72px;
}

.more-btn {
  border: none;
  background: transparent;
  color: #efc79b;
  font-size: 20px;
  font-weight: 600;
  line-height: 1;
}

.shortcut-section {
  margin-top: 24px;
}

.shortcut-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  padding: 28px 14px 24px;
  border-radius: 12px;
}

.shortcut-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 132px;
  border: none;
  background: transparent;
  color: #efc79b;
  font-size: 24px;
  font-weight: 600;
  line-height: 1.2;
  text-align: center;
}

.shortcut-icon {
  width: 72px;
  height: 72px;
  margin-bottom: 16px;
  object-fit: contain;
}

.section-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 26px;
  height: 58px;
  background: linear-gradient(90deg, rgba(38, 39, 45, 0.96), rgba(48, 49, 55, 0.82));
}

.title-left {
  display: flex;
  align-items: center;
}

.title-line {
  width: 4px;
  height: 28px;
  margin: 0 16px 0 18px;
  background: #efc79b;
}

.title-text {
  color: #efb47c;
  font-size: 20px;
  font-weight: 700;
}

.secondary {
  padding-right: 18px;
  color: #a58871;
}

.dating-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-top: 24px;
}

.dating-card {
  position: relative;
  overflow: hidden;
  min-height: 338px;
  border: 4px solid #766350;
  border-radius: 18px;
  background: #15171d;
}

.dating-image {
  display: block;
  width: calc(100% - 8px);
  height: calc(100% - 8px);
  margin: 4px;
  object-fit: cover;
}

.live-icon {
  position: absolute;
  left: 12px;
  top: 12px;
  width: 36px;
  height: 36px;
  object-fit: contain;
}

.verified-tag {
  position: absolute;
  top: 22px;
  right: -28px;
  padding: 5px 42px;
  background: linear-gradient(90deg, #efcba7, #dcab76);
  color: #111;
  font-size: 18px;
  font-style: italic;
  font-weight: 800;
  transform: rotate(45deg);
}

.card-overlay {
  position: absolute;
  right: 4px;
  bottom: 4px;
  left: 4px;
  padding: 16px 12px 14px;
  background: rgba(0, 0, 0, 0.5);
}

.card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}

.vip-text {
  color: #e6b98a;
  font-size: 20px;
  font-weight: 800;
}

.location-tag {
  padding: 6px 14px;
  border-radius: 16px;
  background: linear-gradient(90deg, #efcba7, #dcab76);
  color: #4a4947;
  font-size: 18px;
  font-weight: 800;
}

.score-row {
  display: flex;
  align-items: center;
  margin-top: 10px;
}

.score-icon {
  width: 24px;
  height: 24px;
  margin-right: 8px;
  object-fit: contain;
}

.score-text {
  color: #fff;
  font-size: 24px;
  font-weight: 800;
}

.customer-service-btn {
  position: fixed;
  right: 20px;
  bottom: 148px;
  z-index: 30;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 84px;
  height: 84px;
  border: none;
  border-radius: 50%;
  background: #ff2f34;
  box-shadow: 0 0 18px rgba(255, 66, 87, 0.55);
}

.customer-service-btn img {
  width: 38px;
  height: 38px;
  object-fit: contain;
}

@keyframes marquee {
  0% {
    transform: translateX(0);
  }

  100% {
    transform: translateX(-50%);
  }
}
</style>
